import moment from 'moment';
import type { TDocumentDefinitions, Content } from 'pdfmake/interfaces';

import { DATE_FORMAT, DATE_TIME_FORMAT } from '@/config';
import { USStates } from '@/constants/address';
import * as form_inputs from '@/constants/form_inputs';
import {
  DisabilitiesCodes,
  EyeDefectsCodes,
  FaceCodes,
  FacialFeaturesCodes,
  HairTypesCodes,
} from '@/constants/users';
import { IAgency } from '@/models/agency';
import { KaseMasterNameInvolvementLabels } from '@/models/kase';
import {
  EyeColor,
  PhoneNumberTypeMap,
  IMasterNamePerson,
} from '@/models/master.names';
import { INote } from '@/models/note';
import { IMasterIndexRelatedItems } from '@/models/related.files';
import { IUser } from '@/models/user';
import {
  DispositionFriendlyNames,
  IWantedPerson,
  ProcessTypeFriendlyNames,
} from '@/models/wanted.person';
import { getCachedBase64Image } from '@/pages/Kases/Components/KasePrintModal/utils/imageHelpers';
import { getPlainTextFromDescription } from '@/pages/Notes/NotesDataGridWrapper';
import { getAddressLocation } from '@/services';
import { formatCountryDisplay, formatHeightDisplay } from '@/utils';
import { formatDateOnly } from '@/utils/datetime';
import { formatPhoneNumber } from '@/utils/pattern';
import { buildReportHeader } from '@/utils/reports/buildReportHeader';

export interface PersonReportContext {
  person: IMasterNamePerson;
  notes: INote[];
  relatedRecords: IMasterIndexRelatedItems[];
  wantedRecords: IWantedPerson[];
  agency: IAgency | null;
  user: IUser | null;
  printedAt: Date;
  photoUrls?: string[];
  documentFiles?: { name: string; size: number; mimeType: string }[];
}

// Helper: render a value or '/' if empty
const val = (v: any): string => {
  if (v === null || v === undefined || v === '') return '/';
  return String(v);
};

// Helper: capitalize first letter, lowercase rest
const capitalize = (s: string): string =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '';

// Helper: map sex code to full word
const formatSex = (sex?: string): string => {
  if (!sex) return '/';
  const mapped =
    form_inputs.GenderList[sex as keyof typeof form_inputs.GenderList];
  if (mapped) return mapped;
  if (sex === 'U') return 'Unknown';
  if (sex === 'R') return 'Not Reported';
  if (sex === 'X') return 'Not Applicable';
  return sex;
};

// Helper: map language codes to full names
const formatLanguages = (codes?: string[]): string => {
  if (!codes?.length) return '/';
  return codes
    .map(
      (code) =>
        form_inputs.LanguageList[
          code as keyof typeof form_inputs.LanguageList
        ] ?? code,
    )
    .join(', ');
};

// Helper: map state code to full name
const formatState = (code?: string): string => {
  if (!code) return '/';
  const state = USStates.find((s) => s.value === code);
  return state ? state.label : code;
};

// Helper: map eye color stored value to display
const formatEyeColor = (eyeColorValue?: string): string => {
  if (!eyeColorValue) return '/';
  // EyeColor enum: key (BLK) = 'Black', stored value could be key or value
  const fromEnum = EyeColor[eyeColorValue as keyof typeof EyeColor];
  if (fromEnum) return fromEnum;
  // If stored as uppercase display name e.g. "BLACK", find it
  const key = Object.keys(EyeColor).find(
    (k) =>
      EyeColor[k as keyof typeof EyeColor].toUpperCase() ===
      eyeColorValue.toUpperCase(),
  );
  if (key) return EyeColor[key as keyof typeof EyeColor];
  return capitalize(eyeColorValue);
};

// Helper: map hair color stored value to display
const formatHairColor = (hairColorValue?: string): string => {
  if (!hairColorValue) return '/';
  // Hair color is stored as display value e.g. "Black"
  // But verify against HairColor map
  const key = Object.keys(form_inputs.HairColor).find(
    (k) =>
      form_inputs.HairColor[
        k as keyof typeof form_inputs.HairColor
      ].toLowerCase() === hairColorValue.toLowerCase(),
  );
  return key
    ? form_inputs.HairColor[key as keyof typeof form_inputs.HairColor]
    : hairColorValue;
};

// Helper: map eye defect codes to full words
const formatEyeDefects = (codes?: string[]): string => {
  if (!codes?.length) return '/';
  return codes
    .map(
      (code) => EyeDefectsCodes[code as keyof typeof EyeDefectsCodes] ?? code,
    )
    .join(', ');
};

// Helper: map face codes to full words
const formatFace = (codes?: string[]): string => {
  if (!codes?.length) return '/';
  return codes
    .map((code) => FaceCodes[code as keyof typeof FaceCodes] ?? code)
    .join(', ');
};

// Helper: map disability codes to full words
const formatDisabilities = (codes?: string[]): string => {
  if (!codes?.length) return '/';
  return codes
    .map(
      (code) =>
        DisabilitiesCodes[code as keyof typeof DisabilitiesCodes] ?? code,
    )
    .join(', ');
};

// Helper: map hair type codes to full words
const formatHairTypes = (codes?: string[]): string => {
  if (!codes?.length) return '/';
  return codes
    .map((code) => HairTypesCodes[code as keyof typeof HairTypesCodes] ?? code)
    .join(', ');
};

// Helper: map facial feature codes to full words
const formatFacialFeatures = (codes?: string[]): string => {
  if (!codes?.length) return '/';
  return codes
    .map(
      (code) =>
        FacialFeaturesCodes[code as keyof typeof FacialFeaturesCodes] ?? code,
    )
    .join(', ');
};

// Helper: format social media platform capitalization
const formatPlatform = (platform: string): string => {
  return capitalize(platform);
};

// Helper: format nationality using NationalityList (key→display)
const formatNationality = (value?: string): string => {
  if (!value) return '/';
  const display =
    form_inputs.NationalityList[
      value as keyof typeof form_inputs.NationalityList
    ];
  if (display) return display;
  return value;
};

// Helper: format citizenOf using CountryDisplayNames
const formatCitizenOf = (value?: string): string => {
  if (!value) return '/';
  return formatCountryDisplay(value, value);
};

// Helper: section header row (full-width, bold, centred, grey background)
const sectionHeader = (title: string): Content => ({
  table: {
    widths: ['*'],
    body: [
      [{ text: title, bold: true, alignment: 'center', fillColor: '#E7EAEC' }],
    ],
  },
  layout: {
    hLineWidth: () => 1,
    vLineWidth: () => 1,
    hLineColor: () => '#000',
    vLineColor: () => '#000',
    paddingLeft: () => 4,
    paddingRight: () => 4,
    paddingTop: () => 3,
    paddingBottom: () => 3,
  },
  margin: [0, 8, 0, 0] as [number, number, number, number],
});

// Helper: data row table (array of cell strings per row)
// All rows are padded/truncated to match the column count of the first row.
const dataTable = (rows: string[][]): Content => {
  const colCount = rows[0]?.length ?? 1;
  const normalizedRows = rows.map((row) => {
    const padded = [...row];
    while (padded.length < colCount) padded.push('');
    return padded.slice(0, colCount);
  });
  return {
    table: {
      widths: normalizedRows[0]?.map(() => '*') ?? ['*'],
      body: normalizedRows.map((row) =>
        row.map((cell) => ({ text: cell, fontSize: 9 })),
      ),
    },
    layout: {
      hLineWidth: () => 1,
      vLineWidth: () => 1,
      hLineColor: () => '#000',
      vLineColor: () => '#000',
      paddingLeft: () => 4,
      paddingRight: () => 4,
      paddingTop: () => 2,
      paddingBottom: () => 2,
    },
    margin: [0, 0, 0, 0] as [number, number, number, number],
  } as Content;
};

export const buildPersonReportDefinition = async (
  ctx: PersonReportContext,
): Promise<TDocumentDefinitions> => {
  const {
    person,
    notes,
    relatedRecords,
    wantedRecords,
    agency,
    user,
    printedAt,
    photoUrls,
    documentFiles,
  } = ctx;

  // ── Logo ──────────────────────────────────────────────────────────────────
  let logo: string | undefined;
  const logoUrl = (agency as any)?.logo?.url;
  if (logoUrl) {
    try {
      logo = await getCachedBase64Image(logoUrl);
    } catch {
      // skip logo silently
    }
  }

  // ── Photos (URLs pre-resolved with SAS tokens by the caller) ────────────
  const photoBase64List: string[] = [];
  for (const url of photoUrls ?? []) {
    try {
      const b64 = await getCachedBase64Image(url);
      if (b64) photoBase64List.push(b64);
    } catch {
      // skip failed photo silently
    }
  }

  // ── Page header (repeats on every page) ──────────────────────────────────
  const header = (): Content => {
    // Report-specific entries (rebuilt per page — pdfMake mutates header nodes)
    const reportEntries: Content[] = [
      {
        text: 'MASTER INDEX RECORD',
        bold: true,
        fontSize: 10,
        alignment: 'left' as const,
      },
      {
        text: person.isVerified ? 'VERIFIED' : 'NOT VERIFIED',
        bold: true,
        fontSize: 9,
        alignment: 'left' as const,
        margin: [0, 2, 0, 6] as [number, number, number, number],
      },
      {
        text: `Printed on: ${moment(printedAt).format(DATE_TIME_FORMAT)}`,
        fontSize: 9,
        alignment: 'left' as const,
      },
      {
        text: `Printed by: ${user?.fullName ?? ''}`,
        fontSize: 9,
        alignment: 'left' as const,
      },
    ];

    return buildReportHeader({
      agency,
      logo,
      reportEntries,
      outerMargin: [20, 10, 20, 30],
    });
  };

  // ── Footer ────────────────────────────────────────────────────────────────
  const footer = (currentPage: number, pageCount: number): Content => ({
    text: `Page ${currentPage} of ${pageCount}`,
    alignment: 'center',
    fontSize: 9,
    margin: [0, 10, 0, 0] as [number, number, number, number],
  });

  // ── Helpers ───────────────────────────────────────────────────────────────
  const mapInvolvement = (code: string): string =>
    KaseMasterNameInvolvementLabels[
      code as keyof typeof KaseMasterNameInvolvementLabels
    ] ?? code;

  const joinInvolvement = (inv: string | string[] | undefined): string => {
    if (!inv) return '/';
    if (Array.isArray(inv)) return inv.map(mapInvolvement).join(', ') || '/';
    return mapInvolvement(inv);
  };

  // ── Phone numbers: all types, formatted without +1 ────────────────────────
  const phoneNumberRows = (person.phoneNumbers ?? []).map((p) => {
    const typeLabel =
      PhoneNumberTypeMap[p.type as keyof typeof PhoneNumberTypeMap] ?? p.type;
    return `${typeLabel}: ${formatPhoneNumber(p.phoneNumber)}`;
  });
  const phoneNumbersText = phoneNumberRows.length
    ? phoneNumberRows.join('\n')
    : '/';

  // ── Addresses: all addresses ──────────────────────────────────────────────
  const addressesText = person.transient
    ? 'Transient'
    : (() => {
        const rows = (person.addresses ?? []).map((addr) => {
          const location = getAddressLocation(addr);
          return `${location || '/'} - ${addr.status ?? ''}`;
        });
        return rows.length ? rows.join('\n') : '/';
      })();

  // ── Section 1: IDENTITY ───────────────────────────────────────────────────
  const identitySection: Content[] = [
    sectionHeader('IDENTITY'),
    dataTable([
      [
        `Suffix: ${val(person.suffix)}`,
        `Last Name: ${val(person.lastName)}`,
        `First Name: ${val(person.firstName)}`,
        `Middle Name: ${val(person.middleName)}`,
      ],
      [
        `Maiden Name: ${val(person.maidenName)}`,
        `Alias Names: ${val(person.aliasName)}`,
        `Nickname: ${val(person.nickName)}`,
        `Date of Birth: ${
          person.birthday ? moment(person.birthday).format(DATE_FORMAT) : '/'
        }`,
        `Place of Birth: ${val(person.placeOfBirth)}`,
      ],
      [
        `Sex: ${formatSex(person.sex)}`,
        `Race: ${val(
          form_inputs.RaceList[
            person.race as keyof typeof form_inputs.RaceList
          ],
        )}`,
        `Religion: ${val(person.religion)}`,
        `Education: ${val(person.education)}`,
      ],
      [
        `SSN: ${val(person.ssn)}`,
        `Nationality: ${formatNationality(person.nationality)}`,
        `Citizen of: ${formatCitizenOf(person.citizenOf)}`,
        `Ethnic origin: ${val(
          form_inputs.EthnicOriginList[
            person.ethnicOrigin as keyof typeof form_inputs.EthnicOriginList
          ],
        )}`,
      ],
      [`Ref #: ${val(person.refNumber)}`, '', '', ''],
    ]),
  ];

  // ── Section 2: PERSONAL INFORMATION ──────────────────────────────────────
  const personalInfoSection: Content[] = [
    sectionHeader('PERSONAL INFORMATION'),
    dataTable([
      [
        `Transient: ${person.transient ? 'Yes' : 'No'}`,
        `Phone numbers: ${phoneNumbersText}`,
      ],
      [`Addresses: ${addressesText}`],
      [
        `Flags: ${
          person.flags
            ?.map((f) => f.flag?.name)
            .filter(Boolean)
            .join(', ') || '/'
        }`,
      ],
    ]),
  ];

  // ── Section 3: NOTES ──────────────────────────────────────────────────────
  const notesSection: Content[] = [];
  if (notes.length > 0) {
    notesSection.push(sectionHeader('NOTES'));
    notesSection.push(
      dataTable(
        notes.map((note) => [
          `Note description: ${
            getPlainTextFromDescription(note.description as any) || '/'
          }`,
          `Created on: ${
            note.createdAt ? moment(note.createdAt).format(DATE_FORMAT) : '/'
          }`,
        ]),
      ),
    );
  }

  // ── Section 4: DRIVER'S LICENSE/ID CARD ──────────────────────────────────
  const dl = person.driverLicense;
  const dlSection: Content[] = [
    sectionHeader("DRIVER'S LICENSE/ID CARD"),
    dataTable([
      [
        `State: ${formatState(dl?.state)}`,
        `ID Number: ${val(dl?.number)}`,
        `Class: ${val(dl?.vehicleClass)}`,
        `Restriction Code(s): ${val(dl?.restrictionCodes)}`,
      ],
      [
        `Endorsement Code(s): ${val(dl?.endorsementCodes)}`,
        `Expiration Date: ${
          dl?.expireDate ? moment(dl.expireDate).format(DATE_FORMAT) : '/'
        }`,
        `Document Number: ${val(dl?.documentNumber)}`,
        `Issue Date: ${
          dl?.issueDate ? moment(dl.issueDate).format(DATE_FORMAT) : '/'
        }`,
      ],
    ]),
  ];

  // ── Section 5: OTHER INFORMATION ─────────────────────────────────────────
  const socialMediaText = person.socialMedia?.length
    ? person.socialMedia
        .map((s) => `${formatPlatform(s.platform)}: ${s.username}`)
        .join('\n')
    : '/';
  const emailsText = person.emails?.length ? person.emails.join('\n') : '/';
  const languagesText = formatLanguages(person.spokenLanguages);

  const otherInfoSection: Content[] = [
    sectionHeader('OTHER INFORMATION'),
    {
      table: {
        widths: ['auto', 'auto', '*', '*', 'auto'],
        body: [
          [
            {
              text: `Marital status:\n${val(person.maritalStatus)}`,
              fontSize: 9,
            },
            { text: `Languages spoken:\n${languagesText}`, fontSize: 9 },
            { text: `Social media:\n${socialMediaText}`, fontSize: 9 },
            { text: `Emails:\n${emailsText}`, fontSize: 9 },
            {
              text: `Gang affiliations:\n${val(person.gangAffiliations)}`,
              fontSize: 9,
            },
          ],
        ],
      },
      layout: {
        hLineWidth: () => 1,
        vLineWidth: () => 1,
        hLineColor: () => '#000',
        vLineColor: () => '#000',
        paddingLeft: () => 4,
        paddingRight: () => 4,
        paddingTop: () => 3,
        paddingBottom: () => 3,
      },
      margin: [0, 0, 0, 0] as [number, number, number, number],
    } as Content,
  ];

  // ── Section 6: EMERGENCY CONTACTS ────────────────────────────────────────
  const emergencySection: Content[] = [];
  if (person.emergencyContacts?.length) {
    emergencySection.push(sectionHeader('EMERGENCY CONTACTS'));
    person.emergencyContacts.forEach((contact) => {
      const ec = contact.emergencyContact;
      const ecPhone = ec?.phoneNumbers?.[0]?.phoneNumber;
      const ecAddress = ec?.addresses?.[0];
      emergencySection.push(
        dataTable([
          [
            `First Name: ${val(ec?.firstName)}`,
            `Middle Name: ${val(ec?.middleName)}`,
            `Last Name: ${val(ec?.lastName)}`,
            `Relationship: ${val(contact.relationship?.relationship)}`,
          ],
          [
            `Phone Number: ${ecPhone ? formatPhoneNumber(ecPhone) : '/'}`,
            `Email: ${val(ec?.emails?.[0])}`,
            `Address: ${ecAddress ? getAddressLocation(ecAddress) : '/'}`,
            `Sub-Address: ${val(ecAddress?.subAddress)}`,
          ],
        ]),
      );
    });
  }

  // ── Section 7: EMPLOYMENT INFORMATION ────────────────────────────────────
  const emp = person.employerInfo;
  const employmentSection: Content[] = [
    sectionHeader('EMPLOYMENT INFORMATION'),
    dataTable([
      [
        `Employer Name: ${val(emp?.name)}`,
        `Employer Phone Number: ${
          emp?.phoneNumber ? formatPhoneNumber(emp.phoneNumber) : '/'
        }`,
      ],
      [
        `Employer Address: ${
          emp?.address ? getAddressLocation(emp.address) : '/'
        }`,
        `Employer Occupation: ${val(emp?.occupation)}`,
      ],
    ]),
  ];

  // ── Section 8: PHYSICAL CHARACTERISTICS ──────────────────────────────────
  const phys = person.physicalCharacteristics;
  const physicalSection: Content[] = [
    sectionHeader('PHYSICAL CHARACTERISTICS'),
    dataTable([
      [
        `Weight (lb): ${val(phys?.weight)}`,
        `Height: ${formatHeightDisplay(phys?.heightExact, '/')}`,
        `Build: ${val(phys?.build)}`,
        `Skin Tone: ${val(phys?.skinTone)}`,
        `Face: ${formatFace((phys as any)?.face)}`,
      ],
      [
        `Disabilities: ${formatDisabilities((phys as any)?.disabilities)}`,
        `Eye color: ${formatEyeColor(phys?.eyeColor)}`,
        `Eye defects: ${formatEyeDefects(phys?.eyeDefects)}`,
        `Hair Color: ${formatHairColor(phys?.hairColor)}`,
        `Hair types: ${formatHairTypes((phys as any)?.hairTypes)}`,
      ],
      [
        `Facial features: ${formatFacialFeatures(
          (phys as any)?.facialFeatures,
        )}`,
        `Scars, Marks and Tattoos: ${val(phys?.bodyMarking)}`,
      ],
    ]),
  ];

  // ── Section 9: RELATED RECORDS ────────────────────────────────────────────
  const relatedSection: Content[] = [];
  if (relatedRecords.length > 0) {
    relatedSection.push(sectionHeader('RELATED RECORDS'));
    relatedSection.push(
      dataTable([
        ['Record number:', 'Location:', 'Date and time:', 'Involvement:'],
        ...relatedRecords.map((r) => [
          val(r.name),
          r.address
            ? getAddressLocation(r.address) || 'No Address'
            : val(r.location),
          r.createdAt ? moment(r.createdAt).format(DATE_TIME_FORMAT) : '/',
          joinInvolvement(r.relationship?.involvement),
        ]),
      ]),
    );
  }

  // ── Section 10: WANTED PERSON RECORDS ────────────────────────────────────
  const wantedSection: Content[] = [];
  if (wantedRecords.length > 0) {
    wantedSection.push(sectionHeader('WANTED PERSON RECORDS'));
    wantedSection.push(
      dataTable([
        ['Record number:', 'Process type:', 'Disposition:', 'Date issued:'],
        ...wantedRecords.map((w) => [
          val(w.recordNumber),
          val(ProcessTypeFriendlyNames[w.processType]),
          w.disposition ? val(DispositionFriendlyNames[w.disposition]) : '/',
          w.dateIssued ? formatDateOnly(w.dateIssued) : '/',
        ]),
      ]),
    );
  }

  // ── Section 11: PHOTOS & FILES ──────────────────────────────────────────
  const photosSection: Content[] = [];
  const filesSection: Content[] = [];

  // Photos: one table per row, wrapped in unbreakable stack to avoid splitting
  if (photoBase64List.length > 0) {
    const photoContent: Content[] = [sectionHeader('PHOTOS')];
    for (let i = 0; i < photoBase64List.length; i += 4) {
      const chunk = photoBase64List.slice(i, i + 4);
      const row: Content[] = chunk.map(
        (b64) =>
          ({
            image: b64,
            fit: [120, 100],
            alignment: 'center',
            margin: [2, 2, 2, 2],
          } as Content),
      );
      while (row.length < 4)
        row.push({ text: '', margin: [2, 2, 2, 2] } as Content);

      photoContent.push({
        table: {
          widths: ['*', '*', '*', '*'],
          body: [row],
        },
        layout: {
          hLineWidth: () => 1,
          vLineWidth: () => 1,
          hLineColor: () => '#000',
          vLineColor: () => '#000',
          paddingLeft: () => 4,
          paddingRight: () => 4,
          paddingTop: () => 4,
          paddingBottom: () => 4,
        },
        margin: [0, 0, 0, 0] as [number, number, number, number],
      } as Content);
    }
    photosSection.push({
      stack: photoContent,
      unbreakable: true,
    } as Content);
  }

  // Files: grid with icon + filename, wrapped in unbreakable stack
  if (documentFiles && documentFiles.length > 0) {
    const fileContent: Content[] = [sectionHeader('FILES')];
    for (let i = 0; i < documentFiles.length; i += 4) {
      const chunk = documentFiles.slice(i, i + 4);
      const row: Content[] = chunk.map(
        (f) =>
          ({
            stack: [
              {
                columns: [
                  { width: '*', text: '' },
                  {
                    width: 50,
                    canvas: [
                      {
                        type: 'rect' as const,
                        x: 0,
                        y: 0,
                        w: 50,
                        h: 50,
                        r: 6,
                        color: '#E74C3C',
                      },
                      {
                        type: 'line' as const,
                        x1: 10,
                        y1: 15,
                        x2: 40,
                        y2: 15,
                        lineWidth: 3,
                        lineColor: '#FFFFFF',
                      },
                      {
                        type: 'line' as const,
                        x1: 10,
                        y1: 25,
                        x2: 40,
                        y2: 25,
                        lineWidth: 3,
                        lineColor: '#FFFFFF',
                      },
                      {
                        type: 'line' as const,
                        x1: 10,
                        y1: 35,
                        x2: 40,
                        y2: 35,
                        lineWidth: 3,
                        lineColor: '#FFFFFF',
                      },
                    ],
                  },
                  { width: '*', text: '' },
                ],
              },
              {
                text: f.name,
                fontSize: 8,
                alignment: 'center',
                margin: [0, 6, 0, 0] as [number, number, number, number],
              },
              {
                text: `${Math.round(f.size / 1024)}KB`,
                fontSize: 7,
                alignment: 'center',
                color: '#666',
              },
            ],
          } as Content),
      );
      while (row.length < 4) row.push({ text: '' } as Content);

      fileContent.push({
        table: {
          widths: ['*', '*', '*', '*'],
          body: [row],
        },
        layout: {
          hLineWidth: () => 1,
          vLineWidth: () => 1,
          hLineColor: () => '#000',
          vLineColor: () => '#000',
          paddingLeft: () => 4,
          paddingRight: () => 4,
          paddingTop: () => 8,
          paddingBottom: () => 8,
        },
        margin: [0, 0, 0, 0] as [number, number, number, number],
      } as Content);
    }
    filesSection.push({
      stack: fileContent,
      unbreakable: true,
    } as Content);
  }

  // ── Assemble document ─────────────────────────────────────────────────────
  const content: Content[] = [
    ...identitySection,
    ...personalInfoSection,
    ...notesSection,
    ...dlSection,
    ...otherInfoSection,
    ...emergencySection,
    ...employmentSection,
    ...physicalSection,
    ...relatedSection,
    ...wantedSection,
    ...photosSection,
    ...filesSection,
  ];

  return {
    content,
    header,
    footer,
    pageSize: 'A4',
    pageOrientation: 'portrait',
    pageMargins: [20, 155, 20, 30],
    defaultStyle: { fontSize: 10, color: '#000' },
    styles: {
      headerAgencyName: { bold: true, fontSize: 11 },
      headerDetails: { fontSize: 9 },
      headerCaseInfo: { bold: true, fontSize: 10, alignment: 'right' },
      headerTimestamp: { fontSize: 9, alignment: 'right' },
    },
  };
};
